import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {ChevronGlyph} from '@/components/ui/icons'
import {Hint} from '@/components/ui/hint'
import {Campo} from '@/components/campo'
import {NativeSelect} from '@/components/native-select'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {BloqueInterno} from '@/components/investigacion/bloque-interno'
import {CausaFila, TipoSelect} from '@/components/investigacion/causa-acciones'
import {ArbolDeCausas} from '@/components/analisis/arbol-de-causas'
import {EspinaDePescado} from '@/components/analisis/espina-de-pescado'
import {
  useAddCausa,
  useCategorias,
  useCausasCatalog,
  usePatchInvestigacion,
  type CausaCategoria,
  type CausaPayload,
  type CausaTipo,
  type Investigacion,
  type Metodologia,
} from '@/hooks/sgsst/incidentes/use-investigacion'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const METODOLOGIAS: Metodologia[] = ['arbol_causas', 'ishikawa', 'cinco_porques']

/** Cambiar de metodología solo reproyecta las causas, nunca las borra. */
function MetodologiaSelector({
  inv,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const patch = usePatchInvestigacion(businessId, incidenteId)
  const hayCausas = inv.causas.length > 0
  return (
    <div data-testid="metodologia-selector">
      <BloqueInterno titulo={t('investigacion.metodologia.titulo')}>
        <Hint>{t('investigacion.metodologia.hint')}</Hint>
        <div className="flex flex-wrap gap-0.5 rounded-lg bg-secondary p-0.5">
          {METODOLOGIAS.map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`metodologia-${m}`}
              aria-pressed={inv.metodologia === m}
              disabled={readOnly || patch.isPending}
              onClick={() => {
                if (m !== inv.metodologia) patch.mutate({metodologia: m})
              }}
              className={cn(
                'inline-flex min-h-11 flex-1 items-center justify-center rounded-md px-3 py-1.5 text-center text-sm transition-colors disabled:opacity-60 sm:min-h-9',
                inv.metodologia === m
                  ? 'bg-card font-medium text-primary shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              )}>
              {t(`investigacion.metodologia.${m}`)}
            </button>
          ))}
        </div>
        {hayCausas && <Hint testId="metodologia-cambio-aviso">{t('investigacion.metodologia.cambioAviso')}</Hint>}
      </BloqueInterno>
    </div>
  )
}

/** El catálogo del art. 3 sugiere, nunca obliga («Otra causa…»), y se busca en el servidor. */
function CausaSelect({
  businessId,
  categoria,
  code,
  descripcion,
  onCode,
  onDescripcion,
  testId,
}: {
  businessId: string
  categoria?: CausaCategoria
  code: string | null
  descripcion: string
  onCode: (code: string | null, label: string) => void
  onDescripcion: (d: string) => void
  testId: string
}) {
  const {t} = useTranslation()
  const libre = code === null
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 200)
  const {data: catalog} = useCausasCatalog(businessId, debouncedQuery, categoria)
  const selectedLabel = !libre ? (catalog?.find((c) => c.code === code)?.label_es ?? descripcion) : null

  function pick(nextCode: string | null, label: string) {
    onCode(nextCode, label)
    setOpen(false)
    setQuery('')
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setQuery('')
  }

  return (
    <div className="space-y-2">
      <SearchComboboxShell
        title={t('investigacion.causas.elegirCausa')}
        open={open}
        onOpenChange={handleOpenChange}
        trigger={
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            data-testid={`${testId}-select-trigger`}
            className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-sm text-foreground transition-colors hover:border-[color-mix(in_oklch,var(--input),var(--foreground)_18%)] md:h-9">
            <span className="min-w-0 truncate">
              {libre ? t('investigacion.causas.otraCausa') : (selectedLabel ?? t('investigacion.causas.elegirCausa'))}
            </span>
            <ChevronGlyph size={14} className="opacity-60" />
          </button>
        }>
        <Input
          maxLength={tope('BUSCA')}
          placeholder={t('investigacion.causas.buscar')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          autoFocus
          data-testid={`${testId}-select-search`}
          className="h-12 shrink-0 rounded-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
        />
        <ul className="flex-1 overflow-y-auto p-1 md:max-h-56 md:flex-none">
          <li>
            <button
              type="button"
              data-testid={`${testId}-select-option-libre`}
              className="w-full rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5"
              onClick={() => pick(null, '')}>
              {t('investigacion.causas.otraCausa')}
            </button>
          </li>
          {(catalog ?? []).map((item) => (
            <li key={item.code}>
              <button
                type="button"
                data-testid={`${testId}-select-option`}
                className="w-full break-words rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5"
                onClick={() => pick(item.code, item.label_es)}>
                {item.label_es}
              </button>
            </li>
          ))}
          {(catalog ?? []).length === 0 && query.trim() && (
            <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('investigacion.causas.sinResultados')}</li>
          )}
        </ul>
      </SearchComboboxShell>
      {libre && (
        <Textarea
          maxLength={tope('TEXTO')}
          data-testid={`${testId}-descripcion`}
          value={descripcion}
          onChange={(e) => onDescripcion(e.target.value)}
          minRows={2}
          placeholder={t('investigacion.causas.descripcionPlaceholder')}
        />
      )}
    </div>
  )
}

type CausaDraft = ReturnType<typeof useCausaDraft>

function useCausaDraft() {
  const [tipo, setTipo] = useState<CausaTipo>('acto_subestandar')
  const [code, setCode] = useState<string | null>(null)
  const [descripcion, setDescripcion] = useState('')
  const efectiva = code !== null ? descripcion : descripcion.trim()
  const reset = () => {
    setCode(null)
    setDescripcion('')
  }
  return {tipo, setTipo, code, setCode, descripcion, setDescripcion, efectiva, reset}
}

function AgregarCausaButton({
  draft,
  addCausa,
  extra,
  testId,
  readOnly,
}: {
  draft: CausaDraft
  addCausa: ReturnType<typeof useAddCausa>
  extra: () => Partial<CausaPayload>
  testId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const [error, setError] = useState<string | null>(null)
  return (
    <>
      <Button
        size="sm"
        className="min-h-11 sm:min-h-0"
        data-testid={testId}
        disabled={readOnly || draft.efectiva === '' || addCausa.isPending}
        onClick={() => {
          setError(null)
          addCausa.mutate(
            {tipo: draft.tipo, catalogo_code: draft.code, descripcion: draft.efectiva, ...extra()},
            {onSuccess: draft.reset, onError: (err) => setError(translateApiError(err))}
          )
        }}>
        {t('investigacion.causas.agregarSubmit')}
      </Button>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </>
  )
}

function DraftCausaSelect({
  businessId,
  categoria,
  draft,
  testId,
}: {
  businessId: string
  categoria?: CausaCategoria
  draft: CausaDraft
  testId: string
}) {
  return (
    <CausaSelect
      businessId={businessId}
      categoria={categoria}
      code={draft.code}
      descripcion={draft.descripcion}
      onCode={(code, label) => {
        draft.setCode(code)
        draft.setDescripcion(label)
      }}
      onDescripcion={draft.setDescripcion}
      testId={testId}
    />
  )
}

function CausasArbol({
  inv,
  businessId,
  incidenteId,
  readOnly,
  problema,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
  problema: string
}) {
  const {t} = useTranslation()
  const addCausa = useAddCausa(businessId, incidenteId)
  const draft = useCausaDraft()
  const [parent, setParent] = useState<string>('')

  return (
    <div className="space-y-3" data-testid="causas-arbol">
      <ArbolDeCausas causas={inv.causas} problema={problema} />
      {inv.causas.length > 0 && (
        <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
          {t('investigacion.causas.arbolDiagramaTitulo')}
        </p>
      )}
      <ul className="space-y-2">
        {inv.causas.map((c) => (
          <CausaFila key={c.id} causa={c} businessId={businessId} incidenteId={incidenteId} readOnly={readOnly} />
        ))}
        {inv.causas.length === 0 && <p className="text-sm text-muted-foreground">{t('investigacion.causas.vacio')}</p>}
      </ul>
      {!readOnly && (
        <BloqueInterno titulo={t('investigacion.causas.agregar')}>
          <Campo label={t('investigacion.causas.tipoLabel')}>
            <TipoSelect tipo={draft.tipo} onChange={draft.setTipo} testId="causa-tipo" />
          </Campo>
          <Campo label={t('investigacion.causas.cualLabel')}>
            <DraftCausaSelect businessId={businessId} draft={draft} testId="causa" />
          </Campo>
          <Campo label={t('investigacion.causas.colgarDe')} opcional>
            <EntitySearchSelect
              items={inv.causas}
              value={inv.causas.find((c) => c.id === parent) ?? null}
              getKey={(c) => c.id}
              filterText={(c) => c.descripcion}
              renderOption={(c) => <span className="line-clamp-2">{c.descripcion}</span>}
              renderTriggerValue={(c) => <span className="line-clamp-1">{c.descripcion}</span>}
              placeholder={t('investigacion.causas.colgarDeRaiz')}
              searchPlaceholder={t('investigacion.causas.buscar')}
              noResultsLabel={t('investigacion.causas.sinResultados')}
              clearLabel={t('investigacion.causas.colgarDeRaiz')}
              testId="causa-parent"
              onPick={(c) => setParent(c?.id ?? '')}
            />
          </Campo>
          <AgregarCausaButton
            draft={draft}
            addCausa={addCausa}
            extra={() => ({parent_causa_id: parent || null})}
            testId="causa-add"
            readOnly={readOnly}
          />
        </BloqueInterno>
      )}
    </div>
  )
}

function CausasPorques({
  inv,
  businessId,
  incidenteId,
  readOnly,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const addCausa = useAddCausa(businessId, incidenteId)
  const draft = useCausaDraft()
  const cadena = inv.causas
  const ultimo = cadena.length > 0 ? cadena[cadena.length - 1] : null

  return (
    <div className="space-y-3" data-testid="causas-porques">
      <Hint>{t('investigacion.causas.porquesIntro')}</Hint>
      <ol className="space-y-2">
        {cadena.map((c, i) => (
          <CausaFila
            key={c.id}
            causa={c}
            businessId={businessId}
            incidenteId={incidenteId}
            readOnly={readOnly}
            numero={i + 1}
          />
        ))}
        {cadena.length === 0 && <p className="text-sm text-muted-foreground">{t('investigacion.causas.vacio')}</p>}
      </ol>
      {!readOnly && (
        <BloqueInterno
          titulo={
            ultimo
              ? t('investigacion.causas.porquesSiguiente', {n: cadena.length + 1})
              : t('investigacion.causas.porquesInicial')
          }>
          <Campo label={t('investigacion.causas.tipoLabel')}>
            <TipoSelect tipo={draft.tipo} onChange={draft.setTipo} testId="porque-tipo" />
          </Campo>
          <Campo label={t('investigacion.causas.cualLabel')}>
            <DraftCausaSelect businessId={businessId} draft={draft} testId="porque" />
          </Campo>
          <AgregarCausaButton
            draft={draft}
            addCausa={addCausa}
            extra={() => ({parent_causa_id: ultimo ? ultimo.id : null})}
            testId="porque-add"
            readOnly={readOnly}
          />
        </BloqueInterno>
      )}
    </div>
  )
}

function CausasIshikawa({
  inv,
  businessId,
  incidenteId,
  readOnly,
  problema,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
  problema: string
}) {
  const {t} = useTranslation()
  const addCausa = useAddCausa(businessId, incidenteId)
  const draft = useCausaDraft()
  const [categoria, setCategoria] = useState<CausaCategoria>('persona')
  const {data: categorias} = useCategorias(businessId)

  const espinas = (categorias ?? [])
    .map((cat) => ({
      code: cat.code,
      label: cat.label_es,
      causas: inv.causas.filter((c) => c.categoria === cat.code),
    }))
    .filter((esp) => esp.causas.length > 0)
  const sinCategoria = inv.causas.filter((c) => c.categoria === null)
  const grupos =
    sinCategoria.length > 0
      ? [...espinas, {code: 'sin', label: t('investigacion.categoria.sin'), causas: sinCategoria}]
      : espinas

  return (
    <div className="space-y-3" data-testid="causas-ishikawa">
      <Hint>{t('investigacion.causas.ishikawaIntro')}</Hint>
      {grupos.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
          {t('investigacion.causas.ishikawaVacio')}
        </p>
      ) : (
        <>
          <EspinaDePescado
            problema={problema}
            espinas={grupos.map((esp) => ({
              key: esp.code,
              label: esp.label,
              causas: esp.causas.map((c) => c.descripcion),
            }))}
          />
          <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
            {t('investigacion.causas.verTextoCompleto')}
          </p>
          <div className="space-y-2">
            {grupos.map((esp) => (
              <div
                key={esp.code}
                className="rounded-lg border border-l-[3px] border-border border-l-primary p-3"
                data-testid={`espina-${esp.code}`}>
                <p className="font-mono text-[10px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                  {esp.label}
                </p>
                {esp.code === 'sin' && !readOnly && (
                  <Hint className="mt-1">{t('investigacion.causas.sinCategoriaInvita')}</Hint>
                )}
                <ul className="mt-2 space-y-2">
                  {esp.causas.map((c) => (
                    <CausaFila
                      key={c.id}
                      causa={c}
                      businessId={businessId}
                      incidenteId={incidenteId}
                      readOnly={readOnly}
                      conCategoria
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </>
      )}
      {!readOnly && (
        <BloqueInterno titulo={t('investigacion.causas.agregar')}>
          <Campo label={t('investigacion.causas.espinaLabel')}>
            <NativeSelect
              data-testid="espina-categoria"
              value={categoria}
              onChange={(e) => {
                setCategoria(e.target.value as CausaCategoria)
                draft.reset()
              }}>
              {(categorias ?? []).map((cat) => (
                <option key={cat.code} value={cat.code}>
                  {cat.label_es}
                </option>
              ))}
            </NativeSelect>
          </Campo>
          <Campo label={t('investigacion.causas.tipoLabel')}>
            <TipoSelect tipo={draft.tipo} onChange={draft.setTipo} testId="espina-tipo" />
          </Campo>
          <Campo label={t('investigacion.causas.cualLabel')}>
            <DraftCausaSelect businessId={businessId} categoria={categoria} draft={draft} testId="espina" />
          </Campo>
          <AgregarCausaButton
            draft={draft}
            addCausa={addCausa}
            extra={() => ({categoria})}
            testId="espina-add"
            readOnly={readOnly}
          />
        </BloqueInterno>
      )}
    </div>
  )
}

export function PasoCausas({
  inv,
  businessId,
  incidenteId,
  readOnly,
  problema,
}: {
  inv: Investigacion
  businessId: string
  incidenteId: string
  readOnly: boolean
  /** Lo que va en la cabeza del pescado: el evento que se está investigando. */
  problema: string
}) {
  return (
    <div className="space-y-4" data-testid="paso-causas">
      <MetodologiaSelector inv={inv} businessId={businessId} incidenteId={incidenteId} readOnly={readOnly} />
      {inv.metodologia === 'cinco_porques' && (
        <CausasPorques inv={inv} businessId={businessId} incidenteId={incidenteId} readOnly={readOnly} />
      )}
      {inv.metodologia === 'ishikawa' && (
        <CausasIshikawa
          inv={inv}
          businessId={businessId}
          incidenteId={incidenteId}
          readOnly={readOnly}
          problema={problema}
        />
      )}
      {inv.metodologia === 'arbol_causas' && (
        <CausasArbol
          inv={inv}
          businessId={businessId}
          incidenteId={incidenteId}
          readOnly={readOnly}
          problema={problema}
        />
      )}
    </div>
  )
}
